<template>
  <v-row v-if="customer !== undefined && customer !== null" no-gutters>
    <!-- Address Column -->
    <v-col v-if="customer !== undefined && customer !== null" cols="12" sm="6" md="4" class="d-flex flex-column mb-1">
      <div style="font-weight: bold;">
        {{ customer.name }}<span v-if="customerNr"> - {{ customerNr }}</span>
      </div>
      <div v-if="customer.legalAddress">
        <div>{{ customer.legalAddress.addressee }}</div>
        <div v-if="customer.legalAddress.coAddress">
          {{ customer.legalAddress.coAddress }}
        </div>
        <div>{{ customer.legalAddress.streetAddress }}</div>
        <div>
          {{ customer.legalAddress.zipCode }}
          {{ customer.legalAddress.city }}
        </div>
      </div>      
    </v-col>

    <!-- Bankgiro & Nationalidentifier Column -->
    <v-col cols="6" md="4" class="d-flex flex-column justify-center">
      <v-form ref="bankgiroForm" v-model="valid">
        <div v-if="customer.nationalIdentifier" class="d-flex align-center mb-2">
          <v-icon small color="primary" style="vertical-align: middle;">
            mdi-card-account-details
          </v-icon>
          <template v-if="editableOrgnumber">
            <v-text-field
              v-model="customer.nationalIdentifier.regNo"
              label="Organisationsnummer"
              dense
              hide-details
              style="max-width: 140px; margin-left: 4px;"
            />
            <v-btn
              v-if="showOrgnummerUpdateButton"
              color="primary"
              x-small
              class="ml-2"
              @click="updateOrgnummer"
            >
              Uppdatera
            </v-btn>
          </template>
          <template v-else>
            <span style="margin-left: 4px;">Organisationsnummer: {{ customer.nationalIdentifier.regNo }}</span>
          </template>      
        </div>
        <div class="d-flex align-center mt-1">
          <v-icon small color="primary" style="vertical-align: middle;">
            mdi-bank
          </v-icon>
          <template v-if="editableBankgiro">
            <v-text-field
              v-model="customer.bankgiro"
              label="Bankgiro"
              :rules="[atLeastOneRule]"
              dense
              style="max-width: 140px; margin-left: 4px;"
            />
            <v-btn
              v-if="showUpdateButton"
              color="primary"
              x-small
              class="ml-2"
              @click="updateBankgiro"
            >
              Uppdatera
            </v-btn>
          </template>
          <template v-else>
            <span style="margin-left: 4px;">Bankgiro: {{ customer.bankgiro }}</span>
          </template>
        </div>
        <div class="d-flex align-center mt-1">
          <v-icon small color="primary" style="vertical-align: middle;">
            mdi-bank
          </v-icon>
          <template v-if="editableBankgiro">
            <v-text-field
              v-model="customer.bankaccount"
              label="Bankkonto"
              :rules="[atLeastOneRule]"
              dense
              style="max-width: 140px; margin-left: 4px;"
            />
            <v-btn
              v-if="showUpdateBankaccountButton"
              color="primary"
              x-small
              class="ml-2"
              @click="updateBankaccount"
            >
              Uppdatera
            </v-btn>
          </template>
          <template v-else>
            <span style="margin-left: 4px;">Bankkonto: {{ customer.bankaccount }}</span>
          </template>
        </div>
      </v-form>
    </v-col>

    <!-- Email Column -->
    <v-col cols="6" md="4" class="d-flex align-center">
      <div v-if="customer.emailAddress">
        <v-icon small color="primary" style="vertical-align: middle;">
          mdi-email
        </v-icon>
        <span style="margin-left: 4px;">{{ customer.emailAddress }}</span>
      </div>
    </v-col>
    <!-- Receipt Button -->
    <v-col v-if="showReceipt" class="d-flex justify-end">
      <v-btn          
        x-small
        elevation="2"              
        @click.stop="getReciept()"
      >        
        Underlag
      </v-btn>
    </v-col>
  </v-row>
</template>

<script>
 import log from '@/logger'
export default {
  name: 'GuaranteeCustomerInfo',
  props: {
    customer: { type: Object, required: true, default: null },
    customerNr: { type: [String, Number], default: null },
    editableBankgiro: { type: Boolean, default: false }, 
    editableOrgnumber: { type: Boolean, default: false }, 
    addressCols: { type: [String, Number], default: 3 },
    bankCols: { type: [String, Number], default: 3 },
    emailCols: { type: [String, Number], default: 4 },
    showReceipt: { type: Boolean, default: false },
    rules: { type: Array, required: false, default:null }
  },
  data() {
    return {
      originalBankgiro: this.customer.bankgiro,
      originalBankaccount: this.customer.bankaccount,
      originalOrgnummer: this.customer.nationalIdentifier.regNo,    
      valid:false
    }
  },
  computed: {
    showUpdateButton() {
      return this.editableBankgiro && this.customer.bankgiro !== this.originalBankgiro
    },
     showUpdateBankaccountButton() {
      return this.editableBankgiro && this.customer.bankaccount !== this.originalBankaccount
    },
    showOrgnummerUpdateButton() {
      return this.editableOrgnumber && this.customer.nationalIdentifier.regNo !== this.originalOrgnummer
    },
     atLeastOneRule() {
      return () => {
        if (this.customer.bankgiro || this.customer.bankaccount) {
          return true
        }
        return 'Bankgiro eller bankkonto måste vara ifyllt.'
      }
    },
  },
  watch: {
    'customer.bankgiro'(newVal, oldVal) {
      log.debug('Bankgiro changed from', oldVal, 'to', newVal)
    },
    'customer.orgnummer'(newVal, oldVal) {
      log.debug('Orgnummer changed from', oldVal, 'to', newVal)
    }
  },
   mounted() {
    console.log('GuaranteeCustomerInfo mounted') // Debug log
  },
  methods: {
    updateBankgiro() {
      this.$emit('update-bankgiro', this.customer.bankgiro, this.customer)
      this.originalBankgiro = this.customer.bankgiro
    },
     updateBankaccount() {
      this.$emit('update-bankaccount', this.customer.bankaccount, this.customer)
      this.originalBankaccount = this.customer.bankaccount
    },
     updateOrgnummer() {
      this.$emit('update-orgnummer', this.customer.nationalIdentifier.regNo, this.customer)
      this.originalOrgnummer = this.customer.nationalIdentifier.regNo
    },
    getReciept(){
      log.debug('getReciept')
      this.$emit('get-reciept')
    },
    nonButtonsShown(){
      return !this.showUpdateBankaccountButton && 
             !this.showUpdateButton && 
             !this.showOrgnummerUpdateButton
    },
    async validate() {
      // If you use a <v-form> inside GuaranteeCustomerInfo:
      if (this.$refs.bankgiroForm) {
        return this.$refs.bankgiroForm.validate() && this.nonButtonsShown()
      }
      // Or, if you want to validate fields manually:
      // return your validation logic here, return true/false
      return true
    },
  }
}
</script>